import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Eye, MousePointerClick, Send, CheckCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface EmailEvent {
  id: string;
  event_type: string;
  created_at: string;
  clicked_link?: string;
}

interface EmailTrackingStatsProps {
  formSubmissionId: string;
  recipientEmail: string;
}

export const EmailTrackingStats = ({ formSubmissionId, recipientEmail }: EmailTrackingStatsProps) => {
  const [events, setEvents] = useState<EmailEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchEmailEvents();
  }, [formSubmissionId]);

  const fetchEmailEvents = async () => {
    const { data, error } = await supabase
      .from("email_events")
      .select("id, event_type, created_at, clicked_link")
      .eq("form_submission_id", formSubmissionId)
      .order("created_at", { ascending: false });

    if (!error && data) {
      setEvents(data);
    }
    setIsLoading(false);
  };

  if (isLoading) {
    return <div className="text-xs text-muted-foreground">Loading...</div>;
  }

  const hasSent = events.some(e => e.event_type === "email.sent");
  const hasDelivered = events.some(e => e.event_type === "email.delivered");
  const hasOpened = events.some(e => e.event_type === "email.opened");
  const clicks = events.filter(e => e.event_type === "email.clicked");

  return (
    <TooltipProvider>
      <div className="flex gap-2 flex-wrap">
        {hasSent && (
          <Tooltip>
            <TooltipTrigger>
              <Badge variant="outline" className="gap-1">
                <Send className="w-3 h-3" />
                Sent
              </Badge>
            </TooltipTrigger>
            <TooltipContent>Email was sent</TooltipContent>
          </Tooltip>
        )}

        {hasDelivered && (
          <Tooltip>
            <TooltipTrigger>
              <Badge variant="outline" className="gap-1">
                <CheckCircle className="w-3 h-3 text-green-600" />
                Delivered
              </Badge>
            </TooltipTrigger>
            <TooltipContent>Email was delivered</TooltipContent>
          </Tooltip>
        )}

        {hasOpened && (
          <Tooltip>
            <TooltipTrigger>
              <Badge variant="secondary" className="gap-1">
                <Eye className="w-3 h-3 text-blue-600" />
                Opened
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              User opened the email
            </TooltipContent>
          </Tooltip>
        )}

        {clicks.length > 0 && (
          <Tooltip>
            <TooltipTrigger>
              <Badge variant="default" className="gap-1">
                <MousePointerClick className="w-3 h-3" />
                {clicks.length} {clicks.length === 1 ? 'Click' : 'Clicks'}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <div className="max-w-xs">
                <p className="font-semibold mb-1">Clicked links:</p>
                {clicks.slice(0, 3).map((click, i) => (
                  <p key={i} className="text-xs truncate">
                    {click.clicked_link}
                  </p>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        )}

        {!hasSent && events.length === 0 && (
          <Badge variant="secondary" className="text-muted-foreground">
            No tracking data
          </Badge>
        )}
      </div>
    </TooltipProvider>
  );
};
